import React, { useEffect, useMemo, useState } from 'react';
import {
  adminConsoleApi,
  ConsoleApiError,
  CONTENT_CONSOLE_TOKEN_KEY,
  type AdminAnalyticsPayload,
  type AdminContentItem,
  type AdminContentKind,
  type AdminPagination,
  type AdminSummary,
  type AdminUser,
} from './contentConsoleApi';
import AccountWorkspace from './content-console/AccountWorkspace';
import ConsoleTopbar, { type ConsoleModule } from './content-console/ConsoleTopbar';
import ContentWorkspace from './content-console/ContentWorkspace';
import AnalyticsWorkspace from './content-console/AnalyticsWorkspace';
import {
  allowedKinds,
  buildPayload,
  emptyContent,
  emptyManagedUser,
  mapContent,
  mapUser,
  type ConsoleLocale,
  type ContentForm,
  type ManagedPasswordForm,
  type ManagedUserForm,
  type PasswordForm,
  type SelfForm,
} from './content-console/models';
import '../../styles/content-console.css';

export const CONTENT_CONSOLE_ROUTE = '/portal/nebula/future-world-content-console-20260325-x7m9k4q2-admin';

const CONTENT_PAGE_SIZE = 8;
const USER_PAGE_SIZE = 8;

const defaultPagination = (pageSize: number): AdminPagination => ({
  page: 1,
  page_size: pageSize,
  total_items: 0,
  total_pages: 1,
});

const getErrorMessage = (error: unknown, fallback: string) => (error instanceof Error ? error.message : fallback);
const isUnauthorizedError = (error: unknown) => error instanceof ConsoleApiError && error.status === 401;

const CONTENT_CONSOLE_STATE_KEY = 'future_world_content_console_state';

type PersistedConsoleState = {
  module: ConsoleModule;
  locale: ConsoleLocale;
  keyword: string;
  contentPage: number;
  selectedId: number | null;
  userKeyword: string;
  userPage: number;
  selectedUserId: number | null;
  sidebarCollapsed: boolean;
};

const defaultConsoleState: PersistedConsoleState = {
  module: 'account',
  locale: 'zh',
  keyword: '',
  contentPage: 1,
  selectedId: null,
  userKeyword: '',
  userPage: 1,
  selectedUserId: null,
  sidebarCollapsed: false,
};

const getConsoleStateStorageKey = (userId: number) => `${CONTENT_CONSOLE_STATE_KEY}:${userId}`;

const readConsoleState = (userId: number): PersistedConsoleState => {
  try {
    const rawValue = localStorage.getItem(getConsoleStateStorageKey(userId));
    if (!rawValue) return defaultConsoleState;
    const parsed = JSON.parse(rawValue) as Partial<PersistedConsoleState>;
    return {
      module: parsed.module ?? defaultConsoleState.module,
      locale: parsed.locale ?? defaultConsoleState.locale,
      keyword: parsed.keyword ?? defaultConsoleState.keyword,
      contentPage: Math.max(parsed.contentPage ?? defaultConsoleState.contentPage, 1),
      selectedId: parsed.selectedId ?? defaultConsoleState.selectedId,
      userKeyword: parsed.userKeyword ?? defaultConsoleState.userKeyword,
      userPage: Math.max(parsed.userPage ?? defaultConsoleState.userPage, 1),
      selectedUserId: parsed.selectedUserId ?? defaultConsoleState.selectedUserId,
      sidebarCollapsed: parsed.sidebarCollapsed ?? defaultConsoleState.sidebarCollapsed,
    };
  } catch {
    return defaultConsoleState;
  }
};

const writeConsoleState = (userId: number, state: PersistedConsoleState) => {
  localStorage.setItem(getConsoleStateStorageKey(userId), JSON.stringify(state));
};

const MODULE_LABELS: Record<ConsoleModule, string> = {
  news: '新闻管理',
  resource: '资源管理',
  career: '招聘管理',
  'project-cooperation': '项目合作管理',
  analytics: '访问统计',
  account: '账号中心',
};

const MODULE_DESCRIPTIONS: Record<ConsoleModule, string> = {
  news: '用于维护新闻内容、发布状态和语言版本。',
  resource: '用于维护资源内容、分类和服务页展示位。',
  career: '用于维护招聘岗位信息、要求和联系方式。',
  'project-cooperation': '用于维护项目合作页面内容、合作模块和问卷二维码。',
  analytics: '查看网站访问量、地区来源和日、月趋势图。',
  account: '用于维护当前账号资料、密码和后台用户信息。',
};

const CONTENT_MODULES: AdminContentKind[] = ['news', 'resource', 'career', 'project-cooperation'];

const ContentConsolePage: React.FC = () => {
  const [token, setToken] = useState(() => localStorage.getItem(CONTENT_CONSOLE_TOKEN_KEY) ?? '');
  const [user, setUser] = useState<AdminUser | null>(null);
  const [summary, setSummary] = useState<AdminSummary | null>(null);
  const [analytics, setAnalytics] = useState<AdminAnalyticsPayload | null>(null);
  const [module, setModule] = useState<ConsoleModule>(defaultConsoleState.module);
  const [locale, setLocale] = useState<ConsoleLocale>(defaultConsoleState.locale);
  const [content, setContent] = useState<ContentForm>(() => emptyContent('news', 'zh'));
  const [items, setItems] = useState<AdminContentItem[]>([]);
  const [keyword, setKeyword] = useState(defaultConsoleState.keyword);
  const [contentPagination, setContentPagination] = useState<AdminPagination>(() => ({ ...defaultPagination(CONTENT_PAGE_SIZE), page: defaultConsoleState.contentPage }));
  const [selectedId, setSelectedId] = useState<number | null>(defaultConsoleState.selectedId);

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [userKeyword, setUserKeyword] = useState(defaultConsoleState.userKeyword);
  const [userPagination, setUserPagination] = useState<AdminPagination>(() => ({ ...defaultPagination(USER_PAGE_SIZE), page: defaultConsoleState.userPage }));
  const [selectedUserId, setSelectedUserId] = useState<number | null>(defaultConsoleState.selectedUserId);
  const [managedUser, setManagedUser] = useState<ManagedUserForm>(() => emptyManagedUser());
  const [managedPassword, setManagedPassword] = useState<ManagedPasswordForm>({ new_password: '', confirm_password: '' });
  const [selfForm, setSelfForm] = useState<SelfForm>({ username: '', display_name: '' });
  const [selfPassword, setSelfPassword] = useState<PasswordForm>({ current_password: '', new_password: '', confirm_password: '' });

  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(defaultConsoleState.sidebarCollapsed);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  const kinds = useMemo(() => allowedKinds(user), [user]);
  const canManageUsers = Boolean(user?.is_superuser || user?.permissions.can_manage_users);
  const canManageAnalytics = Boolean(user?.is_superuser || user?.permissions.can_manage_analytics);
  const isAnalyticsModule = module === 'analytics';
  const activeContentKind = CONTENT_MODULES.includes(module as AdminContentKind) ? module as AdminContentKind : null;

  const previewUrl = useMemo(() => {
    if (!activeContentKind) return '';
    if (activeContentKind === 'career' || activeContentKind === 'project-cooperation') {
      if (content.qrImageFile) return URL.createObjectURL(content.qrImageFile);
      return content.clear_qr_image ? '' : (content.qr_image_url ?? '');
    }
    if (content.coverFile) return URL.createObjectURL(content.coverFile);
    return content.clear_cover_image ? '' : (content.cover_image_url ?? '');
  }, [activeContentKind, content.clear_cover_image, content.clear_qr_image, content.coverFile, content.cover_image_url, content.qrImageFile, content.qr_image_url]);

  useEffect(() => () => {
    if (previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => {
    const root = document.getElementById('root');
    document.body.classList.add('content-console-body');
    root?.classList.add('content-console-root');
    return () => {
      document.body.classList.remove('content-console-body');
      root?.classList.remove('content-console-root');
    };
  }, []);

  useEffect(() => {
    const syncSidebarByViewport = () => {
      if (window.innerWidth >= 1024) setMobileSidebarOpen(false);
    };
    syncSidebarByViewport();
    window.addEventListener('resize', syncSidebarByViewport);
    return () => window.removeEventListener('resize', syncSidebarByViewport);
  }, []);

  const clearFeedback = () => {
    setMessage('');
    setError('');
  };

  const resetManagedAccountState = () => {
    setSelectedUserId(null);
    setManagedUser(emptyManagedUser());
    setManagedPassword({ new_password: '', confirm_password: '' });
  };

  const logout = (nextError = '') => {
    localStorage.removeItem(CONTENT_CONSOLE_TOKEN_KEY);
    setToken('');
    setUser(null);
    setSummary(null);
    setAnalytics(null);
    setItems([]);
    setUsers([]);
    setSelectedId(null);
    setContent(emptyContent('news', locale));
    setKeyword('');
    setUserKeyword('');
    setContentPagination(defaultPagination(CONTENT_PAGE_SIZE));
    setUserPagination(defaultPagination(USER_PAGE_SIZE));
    resetManagedAccountState();
    setMessage('');
    setError(nextError);
  };

  const handleUnauthorized = () => logout('登录已过期，请重新登录。');

  const applyUser = (nextUser: AdminUser) => {
    setUser(nextUser);
    setSelfForm({ username: nextUser.username, display_name: nextUser.display_name });
  };

  const refreshSummary = async (currentToken: string) => {
    const summaryResponse = await adminConsoleApi.summary(currentToken);
    setSummary(summaryResponse.summary);
  };

  const refreshAnalytics = async (currentToken: string) => {
    setLoadingAnalytics(true);
    try {
      const analyticsResponse = await adminConsoleApi.analytics(currentToken);
      setAnalytics(analyticsResponse.analytics);
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const loadContent = async (currentToken: string, currentKind: AdminContentKind, language: ConsoleLocale, query = '', page = 1, preferId?: number | null) => {
    setLoadingItems(true);
    try {
      const response = await adminConsoleApi.list(currentKind, currentToken, query, page, CONTENT_PAGE_SIZE, language);
      setItems(response.results);
      setContentPagination(response.pagination);

      if (response.results.length === 0) {
        setSelectedId(null);
        setContent(emptyContent(currentKind, language));
        return;
      }

      const targetId = response.results.some((item) => item.id === preferId) ? preferId : response.results[0].id;
      if (!targetId) return;

      const detail = await adminConsoleApi.detail(currentKind, targetId, currentToken);
      setSelectedId(targetId);
      setContent(mapContent(detail.item));
    } finally {
      setLoadingItems(false);
    }
  };

  const loadUsers = async (currentToken: string, query = '', page = 1, preferId?: number | null) => {
    setLoadingUsers(true);
    try {
      const response = await adminConsoleApi.listUsers(currentToken, query, page, USER_PAGE_SIZE);
      setUsers(response.results);
      setUserPagination(response.pagination);
      if (response.results.length === 0) {
        resetManagedAccountState();
        return;
      }
      const target = response.results.find((item) => item.id === preferId) ?? response.results[0];
      setSelectedUserId(target.id);
      setManagedUser(mapUser(target));
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      clearFeedback();
      setLoading(true);

      try {
        const me = await adminConsoleApi.me(token);
        if (cancelled) return;

        applyUser(me.user);
        const persistedState = readConsoleState(me.user.id);
        const initialKinds = allowedKinds(me.user);
        const allowedModules: ConsoleModule[] = [
          ...initialKinds,
          ...(me.user.is_superuser || me.user.permissions.can_manage_analytics ? ['analytics' as const] : []),
          'account',
        ];
        const initialModule: ConsoleModule = allowedModules.includes(persistedState.module)
          ? persistedState.module
          : (initialKinds.length > 0 ? initialKinds[0] : 'account');

        setModule(initialModule);
        setLocale(persistedState.locale);
        setKeyword(persistedState.keyword);
        setContentPagination((current) => ({ ...current, page: persistedState.contentPage }));
        setSelectedId(persistedState.selectedId);
        setUserKeyword(persistedState.userKeyword);
        setUserPagination((current) => ({ ...current, page: persistedState.userPage }));
        setSelectedUserId(persistedState.selectedUserId);
        setSidebarCollapsed(persistedState.sidebarCollapsed);

        await refreshSummary(token);
        if (CONTENT_MODULES.includes(initialModule as AdminContentKind)) {
          await loadContent(token, initialModule as AdminContentKind, persistedState.locale, persistedState.keyword, persistedState.contentPage, persistedState.selectedId);
        }
        if (initialModule === 'analytics') {
          await refreshAnalytics(token);
        }
        if (me.user.is_superuser || me.user.permissions.can_manage_users) {
          await loadUsers(token, persistedState.userKeyword, persistedState.userPage, persistedState.selectedUserId);
        }
      } catch (bootstrapError) {
        if (!cancelled) {
          if (isUnauthorizedError(bootstrapError)) handleUnauthorized();
          else setError(getErrorMessage(bootstrapError, '控制台初始化失败。'));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (!user) return;
    writeConsoleState(user.id, {
      module,
      locale,
      keyword,
      contentPage: contentPagination.page,
      selectedId,
      userKeyword,
      userPage: userPagination.page,
      selectedUserId,
      sidebarCollapsed,
    });
  }, [user, module, locale, keyword, contentPagination.page, selectedId, userKeyword, userPagination.page, selectedUserId, sidebarCollapsed]);

  useEffect(() => {
    if (!token || !user || !activeContentKind) return;
    let cancelled = false;
    const sync = async () => {
      try {
        await loadContent(token, activeContentKind, locale, keyword, contentPagination.page, selectedId);
      } catch (syncError) {
        if (!cancelled) {
          if (isUnauthorizedError(syncError)) handleUnauthorized();
          else setError(getErrorMessage(syncError, '内容加载失败。'));
        }
      }
    };
    void sync();
    return () => {
      cancelled = true;
    };
  }, [token, user, activeContentKind, locale, keyword, contentPagination.page]);

  useEffect(() => {
    if (!token || !user || !isAnalyticsModule) return;
    let cancelled = false;
    const syncAnalytics = async () => {
      try {
        await refreshAnalytics(token);
      } catch (syncError) {
        if (!cancelled) {
          if (isUnauthorizedError(syncError)) handleUnauthorized();
          else setError(getErrorMessage(syncError, '访问统计加载失败。'));
        }
      }
    };
    void syncAnalytics();
    return () => {
      cancelled = true;
    };
  }, [token, user, isAnalyticsModule, canManageAnalytics]);

  useEffect(() => {
    if (!token || !user || module !== 'account' || !canManageUsers) return;
    let cancelled = false;
    const syncUsers = async () => {
      try {
        await loadUsers(token, userKeyword, userPagination.page, selectedUserId);
      } catch (syncError) {
        if (!cancelled) {
          if (isUnauthorizedError(syncError)) handleUnauthorized();
          else setError(getErrorMessage(syncError, '用户列表加载失败。'));
        }
      }
    };
    void syncUsers();
    return () => {
      cancelled = true;
    };
  }, [token, user, module, canManageUsers, userKeyword, userPagination.page]);

  const selectContentItem = async (id: number) => {
    if (!token || !activeContentKind) return;
    clearFeedback();
    try {
      const detail = await adminConsoleApi.detail(activeContentKind, id, token);
      setSelectedId(id);
      setContent(mapContent(detail.item));
    } catch (detailError) {
      if (isUnauthorizedError(detailError)) handleUnauthorized();
      else setError(getErrorMessage(detailError, '内容详情加载失败。'));
    }
  };

  const saveContent = async () => {
    if (!token || !activeContentKind) return;
    setSaving(true);
    clearFeedback();
    try {
      const response = await adminConsoleApi.save(activeContentKind, token, buildPayload(activeContentKind, content), content.id);
      setSelectedId(response.item.id);
      setContent(mapContent(response.item));
      await refreshSummary(token);
      setMessage(content.id ? '内容已更新。' : '内容已创建。');
      await loadContent(token, activeContentKind, locale, keyword, contentPagination.page, response.item.id);
    } catch (saveError) {
      if (isUnauthorizedError(saveError)) handleUnauthorized();
      else setError(getErrorMessage(saveError, '保存失败。'));
    } finally {
      setSaving(false);
    }
  };

  const deleteContent = async () => {
    if (!token || !activeContentKind || !content.id) return;
    if (!window.confirm('确定删除这条内容吗？')) return;
    setSaving(true);
    clearFeedback();
    try {
      await adminConsoleApi.remove(activeContentKind, token, content.id);
      await refreshSummary(token);
      setMessage('内容已删除。');
      await loadContent(token, activeContentKind, locale, keyword, contentPagination.page);
    } catch (removeError) {
      if (isUnauthorizedError(removeError)) handleUnauthorized();
      else setError(getErrorMessage(removeError, '删除失败。'));
    } finally {
      setSaving(false);
    }
  };

  const saveOwnAccount = async () => {
    if (!token) return;
    setSaving(true);
    clearFeedback();
    try {
      const response = await adminConsoleApi.updateMyAccount(token, selfForm);
      applyUser(response.user);
      setMessage('账号资料已更新。');
    } catch (updateError) {
      if (isUnauthorizedError(updateError)) handleUnauthorized();
      else setError(getErrorMessage(updateError, '账号资料更新失败。'));
    } finally {
      setSaving(false);
    }
  };

  const changeOwnPassword = async () => {
    if (!token) return;
    if (selfPassword.new_password !== selfPassword.confirm_password) {
      setError('两次输入的新密码不一致。');
      return;
    }
    setSaving(true);
    clearFeedback();
    try {
      await adminConsoleApi.changeMyPassword(token, { current_password: selfPassword.current_password, new_password: selfPassword.new_password });
      setSelfPassword({ current_password: '', new_password: '', confirm_password: '' });
      setMessage('密码已更新。');
    } catch (passwordError) {
      if (isUnauthorizedError(passwordError)) handleUnauthorized();
      else setError(getErrorMessage(passwordError, '密码更新失败。'));
    } finally {
      setSaving(false);
    }
  };

  const saveManagedUser = async () => {
    if (!token) return;
    setSaving(true);
    clearFeedback();
    try {
      if (managedUser.id) {
        await adminConsoleApi.updateUser(token, managedUser.id, {
          username: managedUser.username,
          display_name: managedUser.display_name,
          is_active: managedUser.is_active,
          is_superuser: managedUser.is_superuser,
          can_manage_news: managedUser.can_manage_news,
          can_manage_resources: managedUser.can_manage_resources,
          can_manage_careers: managedUser.can_manage_careers,
          can_manage_project_cooperation: managedUser.can_manage_project_cooperation,
          can_manage_analytics: managedUser.can_manage_analytics,
          can_manage_users: managedUser.can_manage_users,
        });
        await loadUsers(token, userKeyword, userPagination.page, managedUser.id);
        setMessage('用户已更新。');
      } else {
        const created = await adminConsoleApi.createUser(token, managedUser);
        await loadUsers(token, userKeyword, 1, created.user.id);
        setUserPagination((current) => ({ ...current, page: 1 }));
        setMessage('用户已创建。');
      }
      await refreshSummary(token);
    } catch (userSaveError) {
      if (isUnauthorizedError(userSaveError)) handleUnauthorized();
      else setError(getErrorMessage(userSaveError, '用户保存失败。'));
    } finally {
      setSaving(false);
    }
  };

  const resetManagedPassword = async () => {
    if (!token || !managedUser.id) return;
    if (managedPassword.new_password !== managedPassword.confirm_password) {
      setError('两次输入的新密码不一致。');
      return;
    }
    setSaving(true);
    clearFeedback();
    try {
      await adminConsoleApi.setUserPassword(token, managedUser.id, managedPassword.new_password);
      setManagedPassword({ new_password: '', confirm_password: '' });
      setMessage('用户密码已重置。');
    } catch (resetError) {
      if (isUnauthorizedError(resetError)) handleUnauthorized();
      else setError(getErrorMessage(resetError, '密码重置失败。'));
    } finally {
      setSaving(false);
    }
  };

  const pageHeader = module === 'account'
    ? {
        title: MODULE_LABELS[module],
        description: MODULE_DESCRIPTIONS[module],
        stats: [
          { label: '当前用户', value: user?.display_name || '--' },
          { label: '后台用户', value: `${userPagination.total_items}` },
          { label: '用户管理', value: canManageUsers ? '已启用' : '未启用' },
        ],
      }
    : isAnalyticsModule
      ? {
          title: MODULE_LABELS[module],
          description: MODULE_DESCRIPTIONS[module],
          stats: [
            { label: '累计访问', value: `${analytics?.overview.total_visits ?? 0}` },
            { label: '累计访客', value: `${analytics?.overview.unique_visitors ?? 0}` },
            { label: '今日访问', value: `${analytics?.overview.today_visits ?? 0}` },
          ],
        }
      : {
          title: MODULE_LABELS[module],
          description: MODULE_DESCRIPTIONS[module],
          stats: [
            { label: '语言', value: locale.toUpperCase() },
            { label: '版本数', value: `${contentPagination.total_items}` },
            { label: '页码', value: `${contentPagination.page}/${contentPagination.total_pages}` },
          ],
        };

  if (!token || !user) {
    return (
      <div className="content-console-page">
        <div className="content-console-login-shell">
          <form
            className="content-console-login-card"
            onSubmit={async (event) => {
              event.preventDefault();
              clearFeedback();
              setLoading(true);
              try {
                const response = await adminConsoleApi.login(loginForm.username, loginForm.password);
                localStorage.setItem(CONTENT_CONSOLE_TOKEN_KEY, response.token);
                setToken(response.token);
              } catch (loginError) {
                setError(getErrorMessage(loginError, '登录失败。'));
              } finally {
                setLoading(false);
              }
            }}
          >
            <div className="content-console-brand-block">
              <div>
                <span className="content-console-kicker">Future World 控制台</span>
                <h1>内容与账号管理后台</h1>
                <p>用于统一维护新闻、资源、招聘、项目合作和后台账号数据。</p>
              </div>
            </div>
            <label>
              <span>用户名</span>
              <input value={loginForm.username} onChange={(event) => setLoginForm((current) => ({ ...current, username: event.target.value }))} />
            </label>
            <label>
              <span>密码</span>
              <input type="password" value={loginForm.password} onChange={(event) => setLoginForm((current) => ({ ...current, password: event.target.value }))} />
            </label>
            {error ? <div className="content-console-feedback is-error">{error}</div> : null}
            <button type="submit" className="content-console-primary-button is-block" disabled={loading}>{loading ? '登录中...' : '进入控制台'}</button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className={['content-console-page', sidebarCollapsed ? 'is-sidebar-collapsed' : '', mobileSidebarOpen ? 'has-mobile-sidebar-open' : ''].filter(Boolean).join(' ')}>
      {mobileSidebarOpen ? <button type="button" className="content-console-mobile-backdrop" aria-label="关闭导航遮罩" onClick={() => setMobileSidebarOpen(false)} /> : null}

      <div className="content-console-app-shell">
        <ConsoleTopbar
          user={user}
          module={module}
          sidebarCollapsed={sidebarCollapsed}
          mobileSidebarOpen={mobileSidebarOpen}
          contentKinds={kinds}
          onModuleChange={(nextModule) => {
            setModule(nextModule);
            clearFeedback();
            if (CONTENT_MODULES.includes(nextModule as AdminContentKind)) {
              setSelectedId(null);
              setContent(emptyContent(nextModule as AdminContentKind, locale));
              setContentPagination((current) => ({ ...current, page: 1 }));
            }
          }}
          onToggleCollapse={() => setSidebarCollapsed((current) => !current)}
          onToggleMobileSidebar={() => setMobileSidebarOpen((current) => !current)}
          onCloseMobileSidebar={() => setMobileSidebarOpen(false)}
          onLogout={() => logout()}
        />

        <div className="content-console-main-shell">
          <section className="content-console-page-header">
            <div className="content-console-page-header-copy">
              <span className="content-console-kicker">管理工作台</span>
              <h2>{pageHeader.title}</h2>
              <p>{pageHeader.description}</p>
            </div>
            <div className="content-console-page-header-meta">
              {pageHeader.stats.map((item) => (
                <article key={item.label} className="content-console-page-header-stat">
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </article>
              ))}
            </div>
          </section>

          {summary ? (
            <section className="content-console-summary-grid is-five-column">
              <article className="content-console-summary-card">
                <span>新闻分组</span>
                <strong>{summary.news_group_count}</strong>
                <p>已发布 {summary.published_news_group_count} / 版本 {summary.news_version_count}</p>
              </article>
              <article className="content-console-summary-card">
                <span>资源分组</span>
                <strong>{summary.resource_group_count}</strong>
                <p>已发布 {summary.published_resource_group_count} / 版本 {summary.resource_version_count}</p>
              </article>
              <article className="content-console-summary-card">
                <span>招聘分组</span>
                <strong>{summary.career_group_count}</strong>
                <p>已发布 {summary.published_career_group_count} / 版本 {summary.career_version_count}</p>
              </article>
              <article className="content-console-summary-card">
                <span>合作分组</span>
                <strong>{summary.project_cooperation_group_count}</strong>
                <p>已发布 {summary.published_project_cooperation_group_count} / 版本 {summary.project_cooperation_version_count}</p>
              </article>
              <article className="content-console-summary-card">
                <span>后台用户</span>
                <strong>{summary.console_user_count}</strong>
                <p>拥有后台访问权限的用户</p>
              </article>
            </section>
          ) : null}

          {message || error ? <div className={`content-console-feedback${error ? ' is-error' : ' is-success'}`}>{error || message}</div> : null}

          {activeContentKind ? (
            <ContentWorkspace
              kind={activeContentKind}
              keyword={keyword}
              items={items}
              totalItems={contentPagination.total_items}
              currentPage={contentPagination.page}
              totalPages={contentPagination.total_pages}
              selectedId={selectedId}
              content={content}
              locale={locale}
              loading={loading}
              loadingItems={loadingItems}
              saving={saving}
              previewUrl={previewUrl}
              onKeywordChange={(value) => {
                setKeyword(value);
                setContentPagination((current) => ({ ...current, page: 1 }));
              }}
              onPageChange={(page) => setContentPagination((current) => ({ ...current, page }))}
              onSelectItem={selectContentItem}
              onCreateNew={() => {
                setSelectedId(null);
                setContent(emptyContent(activeContentKind, locale));
              }}
              onDelete={deleteContent}
              onSave={saveContent}
              setContent={setContent}
              setLocale={(nextLocale) => {
                setLocale(nextLocale);
                setSelectedId(null);
                setContent(emptyContent(activeContentKind, nextLocale));
                setContentPagination((current) => ({ ...current, page: 1 }));
              }}
            />
          ) : isAnalyticsModule ? (
            <AnalyticsWorkspace
              analytics={analytics}
              loading={loading || loadingAnalytics}
              onRefresh={() => refreshAnalytics(token)}
            />
          ) : (
            <AccountWorkspace
              user={user}
              canManageUsers={canManageUsers}
              users={users}
              userKeyword={userKeyword}
              totalUsers={userPagination.total_items}
              userCurrentPage={userPagination.page}
              userTotalPages={userPagination.total_pages}
              selectedUserId={selectedUserId}
              managedUser={managedUser}
              managedPassword={managedPassword}
              selfForm={selfForm}
              selfPassword={selfPassword}
              loadingUsers={loadingUsers}
              saving={saving}
              setSelfForm={setSelfForm}
              setSelfPassword={setSelfPassword}
              setManagedUser={setManagedUser}
              setManagedPassword={setManagedPassword}
              onUserKeywordChange={(value) => {
                setUserKeyword(value);
                setUserPagination((current) => ({ ...current, page: 1 }));
              }}
              onUserPageChange={(page) => setUserPagination((current) => ({ ...current, page }))}
              onSaveSelf={saveOwnAccount}
              onChangeOwnPassword={changeOwnPassword}
              onRefreshUsers={() => loadUsers(token, userKeyword, userPagination.page, selectedUserId)}
              onCreateUser={resetManagedAccountState}
              onSelectUser={(selectedUser) => {
                setSelectedUserId(selectedUser.id);
                setManagedUser(mapUser(selectedUser));
                setManagedPassword({ new_password: '', confirm_password: '' });
              }}
              onSaveUser={saveManagedUser}
              onResetUserPassword={resetManagedPassword}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ContentConsolePage;
